import React from "react"
import { Check, Plus } from "lucide-react"

import { type NetworkSlug } from "@/lib/networks"
import { META_TEXT } from "@/lib/ui-tokens"
import { cn } from "@/lib/utils"
import { NetworkBadge } from "@/shadcn/components/ui/networks"
import {
  ServiceMark,
  type ServiceIconName,
} from "@/shadcn/components/ui/service-mark"

/**
 * The destination tile — a circular account avatar with its network badge, the
 * account name, and an optional subtype line.
 *
 * Lives here rather than inside CreateAutomation because the same tile is the
 * app's existing `SocialButton`: every output picker (route editor, post tool,
 * onboarding, the social modal) draws an avatar + brand badge + name, and this
 * is the new-flow drawing of it. Keeping it in the wizard file would have meant
 * a second copy the first time another surface adopted the style.
 *
 * Layout only: no data fetching and no selection state. A
 * caller that wants the tile clickable passes `onSelect`; without it the tile is
 * inert markup, which is what the wizard preview needs and what a legacy list
 * that already handles clicks on its own `<li>` wants too.
 */

export type OutputAccount = {
  id: string
  name: string
  /** null when the account's service has no brand mark (renders unbadged). */
  network: NetworkSlug | null
  /** Initials in the avatar, and the fallback when `avatarUrl` is absent or 404s. */
  initials: string
  /** Profile image from the backend. */
  avatarUrl?: string
  /** Optional third line, e.g. "Page" / "Person" / "62% full". */
  subtype?: string
  selected?: boolean
  /** Switched off, by the user or the system — desaturated avatar and badge. */
  inactive?: boolean
  /**
   * Switched off BY THE SYSTEM and needing a reconnect, which is a narrower
   * thing than `inactive` and the only one a person has to act on. Drawn as the
   * broken-link overlay the legacy socials list has always used.
   */
  disconnected?: boolean
  /**
   * Replaces the network badge — for a role the account is in rather than the
   * network it posts to. Currently just `retweet`, worn by an X account that
   * retweets the main tweeter instead of posting on its own.
   */
  badgeIcon?: ServiceIconName
  badgeLabel?: string
  /**
   * Not selectable, with `disabledReason` as its tooltip.
   *
   * The design has no frame for this, but prod gates some combinations and
   * dropping the gate would let the flow build a route the backend refuses: an
   * output that is also the input posts in a loop, a YouTube input can't fan out
   * to the short-form video networks, and an automation takes one YouTube
   * channel. Drawn as a dimmed, inert tile — the same hover-only explanation the
   * legacy list gave.
   */
  disabled?: boolean
  disabledReason?: string
}

/**
 * Avatar fallback for an account name: up to two initials, `@` dropped and
 * handles split on their separators so "dlvrit.demo" reads "DD" rather than "D".
 *
 * Here rather than at the call site because every adopter of the tile needs the
 * same string from the same account records, and two implementations would put
 * different initials on the same account in different parts of the app.
 */
export function initialsFromName(name?: string): string {
  return String(name ?? "")
    .replace(/^@/, "")
    .split(/[\s._·-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word.charAt(0))
    .join("")
    .toUpperCase()
}

/**
 * The broken-link mark, inline rather than `<img src=".../misc/disconnected.svg">`.
 *
 * Same artwork as the asset the legacy avatar overlays (`ui/src/img/misc/`,
 * a FontAwesome unlink filled #606060 and stroked 2px white): the fill and the
 * outline together are the design, and a stroke-only icon — lucide's `Unlink`
 * was tried here — reads as a different mark.
 *
 * Inline because nothing else in this tile comes off the asset host. The brand
 * badges are font glyphs, so an `<img>` would make this the first thing needing
 * `assetBaseUrl` plumbed through the tile and every one of its call sites, for
 * one 800-byte path. The trade is that a redraw of the .svg does not reach here.
 *
 * Colours are the designer's literal values and not tokens. The white is an
 * outline for contrast against an arbitrary profile photo, not a surface colour,
 * so it should not follow the theme.
 */
function BrokenLinkMark({ size }: { size: number }) {
  return (
    <svg
      viewBox="0 0 38 38.163"
      width={size}
      height={size}
      aria-hidden
      focusable="false"
    >
      <path
        d="M21.381,28.54a.844.844,0,0,1,0,1.193L18.24,32.875A10.688,10.688,0,0,1,3.125,17.76l3.141-3.141a.844.844,0,0,1,1.193,0L10.244,17.4a.844.844,0,0,1,0,1.193L7.1,21.738A5.063,5.063,0,0,0,14.262,28.9L17.4,25.756a.844.844,0,0,1,1.193,0l2.784,2.784ZM17.4,10.244a.844.844,0,0,0,1.193,0L21.738,7.1a5.063,5.063,0,0,1,7.16,7.159L25.756,17.4a.844.844,0,0,0,0,1.193l2.784,2.784a.844.844,0,0,0,1.193,0l3.141-3.141A10.688,10.688,0,0,0,17.76,3.125L14.619,6.266a.844.844,0,0,0,0,1.193L17.4,10.244ZM33.915,35.506l1.591-1.591a1.687,1.687,0,0,0,0-2.386L4.472.494a1.687,1.687,0,0,0-2.386,0L.494,2.085a1.687,1.687,0,0,0,0,2.386L31.528,35.506a1.688,1.688,0,0,0,2.386,0Z"
        transform="translate(1 1)"
        fill="#606060"
        stroke="#fff"
        strokeWidth="2"
      />
    </svg>
  )
}

/**
 * Avatar + network badge + selected check, at any size.
 *
 * `badgeSize` defaults to ~46% of the avatar edge, which reproduces the drawn
 * 22px badge on the 48px tile. The review step's 26px stack wants a 15px badge
 * (not the 12px that ratio gives), so it passes its own.
 *
 * The badge is anchored to the avatar's edges rather than offset from its
 * top-left, so a resize can't silently misalign it — the earlier
 * `top-[29px]/left-[29px]` values were only correct at exactly 48px.
 */
export function OutputAvatar({
  network,
  initials,
  avatarUrl,
  size = 48,
  badgeSize = Math.round(size * 0.46),
  badgeIcon,
  badgeLabel,
  selected = false,
  inactive = false,
  disconnected = false,
  textClassName = "tw:text-sm",
  className,
}: {
  network?: NetworkSlug | null
  initials: string
  avatarUrl?: string
  /** Edge of the avatar circle, in px. */
  size?: number
  badgeSize?: number
  /** Badge override — see OutputAccount.badgeIcon. */
  badgeIcon?: ServiceIconName
  badgeLabel?: string
  selected?: boolean
  inactive?: boolean
  disconnected?: boolean
  /** Initials size — they don't scale with the box on their own. */
  textClassName?: string
  className?: string
}) {
  // Avatar URLs are user data pointing at a network's CDN, so a dead one is
  // routine rather than exceptional; falling back to the initials keeps the tile
  // identifiable instead of showing a broken-image glyph.
  const [imageFailed, setImageFailed] = React.useState(false)
  const showImage = Boolean(avatarUrl) && !imageFailed

  return (
    <span
      className={cn(
        "tw:relative tw:inline-block tw:shrink-0",
        className
      )}
      style={{ width: size, height: size }}
    >
      {selected ? (
        <span
          aria-hidden
          className="tw:absolute tw:-inset-0.5 tw:rounded-full tw:border-2 tw:border-selected"
        />
      ) : null}
      {/*
       * Desaturated rather than faded, matching how the legacy socials list has
       * always drawn a disconnected account (animated-avatar.styl:
       * `mix-blend-mode: luminosity` on the photo, $inactiveIcon #cdcdcd behind
       * the mark). Fading alone read as "dimmed for some reason"; greyscale
       * reads as "this one isn't live".
       *
       * On the avatar and the badge, NOT their shared wrapper: `filter` paints
       * everything inside it, and the wrapper also holds the selection ring and
       * check. A tile that is both inactive and selected — reachable, since the
       * two are independent props and inactive tiles stay clickable — rendered
       * its green affordance in grey and looked unselected.
       *
       * Greyscale only, no opacity, for the same reason. Once the treatment is
       * per-element rather than on the wrapper, a translucent badge sits OVER
       * the avatar and the photo shows through the mark. Legacy doesn't fade
       * either — it swaps the colour and desaturates.
       */}
      <span
        className={cn(
          // bg-muted backs the INITIALS only — see PostAvatar: a transparent
          // network logo let it through as a grey disc.
          "tw:flex tw:size-full tw:items-center tw:justify-center tw:overflow-hidden tw:rounded-full tw:text-muted-foreground",
          !showImage && "tw:bg-muted",
          inactive && "tw:grayscale",
          textClassName
        )}
      >
        {showImage ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={avatarUrl}
            alt=""
            className="tw:size-full tw:object-cover"
            onError={() => setImageFailed(true)}
          />
        ) : (
          initials
        )}
      </span>
      {/*
       * The broken-link overlay, at the half-the-avatar size the legacy list
       * draws it (animated-avatar.styl `.disconnected`: a centred
       * disconnected.svg at `background-size: 50%`).
       *
       * No scrim under it, also as legacy: the mark's own white outline is what
       * makes it read against an arbitrary profile photo, so a scrim would only
       * darken the avatar for nothing.
       *
       * Before the badge and the check in the DOM so both paint above it, which
       * is the legacy stacking too — there the service icon is a later sibling.
       */}
      {disconnected ? (
        <span
          role="img"
          aria-label="Needs reconnecting"
          className="tw:absolute tw:inset-0 tw:flex tw:items-center tw:justify-center"
        >
          <BrokenLinkMark size={Math.round(size * 0.5)} />
        </span>
      ) : null}
      {badgeIcon ? (
        <ServiceMark
          icon={badgeIcon}
          label={badgeLabel ?? ""}
          size={badgeSize}
          className={cn(
            "tw:absolute tw:-right-[3px] tw:-bottom-[3px]",
            inactive && "tw:grayscale"
          )}
        />
      ) : network ? (
        <NetworkBadge
          network={network}
          size={badgeSize}
          className={cn(
            "tw:absolute tw:-right-[3px] tw:-bottom-[3px]",
            inactive && "tw:grayscale"
          )}
        />
      ) : null}
      {/* Fixed 16px: the check only appears on the full-size tile, and shrinking
          it with the avatar would put a 9px tick on the review stack. */}
      {selected ? (
        <span className="tw:absolute tw:-top-[3px] tw:-right-0.5 tw:flex tw:size-4 tw:items-center tw:justify-center tw:rounded-full tw:border-2 tw:border-background tw:bg-selected tw:text-background">
          <Check className="tw:size-2.5" strokeWidth={3} />
        </span>
      ) : null}
    </span>
  )
}

export function OutputTile({
  account,
  size,
  onSelect,
  className,
}: {
  account: OutputAccount
  /** Avatar edge, in px. Defaults to the drawn 48. */
  size?: number
  /** Makes the tile a real button. Omit to render inert markup. */
  onSelect?: (account: OutputAccount) => void
  className?: string
}) {
  // min-w-0 so the name can't set the tile's floor: a grid item's automatic
  // minimum is its min-content width, which for a single-word username is the
  // whole word — enough to push the grid past the viewport.
  const base = "tw:flex tw:min-w-0 tw:flex-col tw:items-center tw:gap-2"

  const content = (
    <>
      <OutputAvatar
        network={account.network}
        initials={account.initials}
        avatarUrl={account.avatarUrl}
        size={size}
        badgeIcon={account.badgeIcon}
        badgeLabel={account.badgeLabel}
        selected={account.selected}
        inactive={account.inactive}
        disconnected={account.disconnected}
      />
      {/* Two lines at mobile: 3-up gives each tile ~88px, where a one-line
          name clipped almost immediately. wrap-anywhere is what makes it work —
          usernames are single unbreakable words, so without it a long one would
          be cut mid-character with no ellipsis instead of wrapping. */}
      <p
        // Dropped while disabled so the wrapper's reason is what the whole tile
        // reports on hover, rather than the name shadowing it over half of it.
        title={account.disabled ? undefined : account.name}
        className="tw:line-clamp-2 tw:w-full tw:text-center tw:text-sm tw:font-medium tw:wrap-anywhere tw:text-foreground tw:sm:line-clamp-1"
      >
        {account.name}
      </p>
      {account.subtype ? (
        <p className={cn("tw:w-full tw:text-center", META_TEXT)}>
          {account.subtype}
        </p>
      ) : null}
    </>
  )

  // A div rather than a disabled <button>: a disabled button shows no title
  // tooltip in Chrome, and the reason is the only thing that explains the tile.
  if (!onSelect || account.disabled) {
    return (
      <div
        className={cn(
          base,
          account.disabled && "tw:cursor-not-allowed tw:opacity-60",
          className
        )}
        title={account.disabledReason}
        aria-disabled={account.disabled || undefined}
      >
        {content}
      </div>
    )
  }

  return (
    <button
      type="button"
      aria-pressed={Boolean(account.selected)}
      onClick={() => onSelect(account)}
      className={cn(
        base,
        "tw:rounded-md tw:outline-none tw:focus-visible:ring-3 tw:focus-visible:ring-ring/50",
        className
      )}
    >
      {content}
    </button>
  )
}

/** The "+ Connect New" tile that opens the connect flow, sized to match. */
export function ConnectNewTile({
  label = "Connect New",
  size = 48,
  onSelect,
  className,
}: {
  label?: string
  size?: number
  onSelect?: () => void
  className?: string
}) {
  const base = "tw:flex tw:min-w-0 tw:flex-col tw:items-center tw:gap-2"

  const content = (
    <>
      <span
        className="tw:flex tw:shrink-0 tw:items-center tw:justify-center tw:rounded-full tw:border tw:border-primary tw:text-primary"
        style={{ width: size, height: size }}
      >
        <Plus className="tw:size-5" />
      </span>
      {/* Not `truncate`: at 3-up mobile the tile is ~88px and "Connect New"
          clips to "Connect N…". It wraps on its own space instead, matching how
          OutputTile handles a long account name. */}
      <p className="tw:w-full tw:text-center tw:text-sm tw:font-medium tw:text-primary">
        {label}
      </p>
    </>
  )

  if (!onSelect) {
    return <div className={cn(base, className)}>{content}</div>
  }

  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        base,
        "tw:rounded-md tw:outline-none tw:focus-visible:ring-3 tw:focus-visible:ring-ring/50",
        className
      )}
    >
      {content}
    </button>
  )
}
